import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  TrendingUp, TrendingDown, DollarSign, FileText, AlertTriangle,
  ChevronRight, Receipt, BarChart3, Briefcase, ArrowLeft, Building2,
  Truck, Landmark, ShieldCheck,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import {
  Card, Pill, Button, KPI, Spinner, EmptyState,
} from "@/components/ui";
import { fmtMoney, fmtMoneyK, fmtDate } from "@/lib/dates";

// =============================================================================
// Commercial Dashboard
// =============================================================================
// Director-only view. The big picture of where the business stands right now:
//   · Pipeline (quotes sent, awaiting decision)
//   · Active project contract value + estimated margin
//   · Outstanding AR (invoices not paid)
//   · Monthly burn rate (OPEX roll-up)
//   · Net position: (accepted quotes + paid AR) − AP spend − monthly burn
// =============================================================================

export default function CommercialDashboardScreen() {
  const user = useRequireAuth();

  const { data: stats, isLoading } = useQuery({
    queryKey: ["commercial_dashboard"],
    enabled: hasRole(user, "director"),
    queryFn: async () => {
      const [quotes, invoicesOut, invoicesPaid, apAll, apPaid, burn, activeProjects, projectFin, payroll, lastRun] = await Promise.all([
        supabase.from("quotes").select("total_incl_gst, status, margin_percent").in("status", ["sent", "accepted"]),
        // T-397 — real statuses (part_paid, not the never-valid pushed_to_xero) +
        // amount_paid so part-paid invoices only count their outstanding slice
        supabase.from("client_invoices").select("total_incl_gst, amount_paid, due_date").in("status", ["sent", "part_paid", "overdue"]),
        // T-416 — nothing ever writes paid_date; payments stamp paid_at. This
        // query returned 0 rows forever → "Revenue +$0.00" despite real cash.
        supabase.from("client_invoices").select("total_incl_gst, paid_at").eq("status", "paid").gte("paid_at", firstOfMonth()),
        // T-397 — ap_invoices uses amount_inc_gst (old selects silently errored → $0)
        supabase.from("ap_invoices").select("amount_inc_gst, status"),
        supabase.from("ap_invoices").select("amount_inc_gst, invoice_date").eq("status", "paid").gte("invoice_date", firstOfMonth()),
        supabase.from("v_company_monthly_burn").select("*").single(),
        supabase.from("projects").select("project_id", { count: "exact", head: true }).in("status", ["live", "mobilising", "acceptance_pending"]),
        supabase.from("v_project_financials").select("*").in("status", ["live", "mobilising", "acceptance_pending"]).order("contract_value", { ascending: false }).limit(10),
        // T-463 — payroll paid this month (company cost = net + PAYG + super)
        supabase.from("pay_runs").select("total_gross, total_super, paid_date, status").eq("status", "paid").gte("paid_date", firstOfMonth()),
        // Most recent recorded run(s), ANY month — so a $0 month-to-date figure
        // can still show when payroll was last actually paid, instead of reading
        // as "we paid nobody".
        supabase.from("pay_runs").select("total_gross, total_super, paid_date").eq("status", "paid").order("paid_date", { ascending: false }).limit(10),
      ]);

      return {
        pipeline_value:    (quotes.data ?? []).filter(q => q.status === "sent").reduce((a, q) => a + (q.total_incl_gst ?? 0), 0),
        pipeline_count:    (quotes.data ?? []).filter(q => q.status === "sent").length,
        accepted_value:    (quotes.data ?? []).filter(q => q.status === "accepted").reduce((a, q) => a + (q.total_incl_gst ?? 0), 0),
        avg_quote_margin:  avgMargin(quotes.data ?? []),
        outstanding_ar:    (invoicesOut.data ?? []).reduce((a, i: any) => a + Number(i.total_incl_gst ?? 0) - Number(i.amount_paid ?? 0), 0),
        overdue_ar:        (invoicesOut.data ?? []).filter((i: any) => i.due_date && new Date(i.due_date) < new Date()).reduce((a, i: any) => a + Number(i.total_incl_gst ?? 0) - Number(i.amount_paid ?? 0), 0),
        ar_count:          (invoicesOut.data ?? []).length,
        revenue_this_month: (invoicesPaid.data ?? []).reduce((a, i) => a + (i.total_incl_gst ?? 0), 0),
        ap_spend_mtd:      (apPaid.data ?? []).reduce((a, i: any) => a + Number(i.amount_inc_gst ?? 0), 0),
        // T-397 — live AP statuses are captured/pending_review (pending_approval isn't one)
        ap_pending:        (apAll.data ?? []).filter((r: any) => r.status === "captured" || r.status === "pending_review").reduce((a: number, i: any) => a + Number(i.amount_inc_gst ?? 0), 0),
        monthly_burn:      (burn.data as any)?.total_monthly_burn_ex_gst ?? 0,
        payroll_mtd:       (payroll.data ?? []).reduce((a: number, r: any) => a + Number(r.total_gross ?? 0) + Number(r.total_super ?? 0), 0),
        last_run:          (() => {
          const rows = (lastRun.data ?? []) as any[];
          if (rows.length === 0) return null;
          const d = rows[0].paid_date;
          // Sum every group paid on that same date (Field staff + Directors etc.)
          const total = rows.filter(r => r.paid_date === d)
            .reduce((a: number, r: any) => a + Number(r.total_gross ?? 0) + Number(r.total_super ?? 0), 0);
          return { paid_date: d as string, total };
        })(),
        active_projects:   activeProjects.count ?? 0,
        project_financials: projectFin.data ?? [],
      };
    },
  });

  if (!hasRole(user, "director")) {
    return (
      <div className="p-6 max-w-md mx-auto">
        <Card><EmptyState title="Directors only" description="The commercial dashboard is restricted to directors." icon={DollarSign} /></Card>
      </div>
    );
  }

  if (isLoading || !stats) return <div className="py-20 flex justify-center"><Spinner /></div>;

  // Net position this month (rough): paid invoices − AP paid − monthly burn
  const net_month = stats.revenue_this_month - stats.ap_spend_mtd - stats.monthly_burn - stats.payroll_mtd;

  return (
    <div className="p-4 md:p-6 max-w-[1400px] mx-auto">
      <Link to="/finance" className="inline-flex items-center gap-1.5 text-[12px] text-text-muted hover:text-accent mb-3">
        <ArrowLeft size={14}/> Finance hub
      </Link>
      <div className="mb-5">
        <h1 className="text-2xl font-semibold tracking-tight">Commercial Dashboard</h1>
        <div className="text-text-muted text-[13px] mt-1">The numbers, right now. Director view.</div>
      </div>

      {/* Top-level KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <KPI
          label="Net position this month"
          value={fmtMoney(net_month)}
          sub="revenue − AP − burn − payroll"
          icon={net_month >= 0 ? TrendingUp : TrendingDown}
          accent={net_month >= 0}
        />
        <KPI label="Pipeline" value={fmtMoney(stats.pipeline_value)} sub={`${stats.pipeline_count} quotes sent`} icon={FileText} />
        <KPI label="Outstanding AR" value={fmtMoney(stats.outstanding_ar)} sub={`${stats.ar_count} invoices`} accent={stats.overdue_ar > 0} />
        <KPI label="Monthly burn" value={fmtMoney(stats.monthly_burn)} sub="fixed OPEX" icon={TrendingDown} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        {/* Revenue / spend breakdown */}
        <Card>
          <div className="p-4 border-b border-line">
            <h3 className="font-semibold">This month — cash flow</h3>
            <div className="text-[11px] text-text-muted mt-0.5">From paid invoices and paid bills only.</div>
          </div>
          <div className="p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-ok" />
                <span className="text-[13px]">Revenue</span>
              </div>
              <span className="font-mono text-[14px] text-ok">+{fmtMoney(stats.revenue_this_month)}</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-danger" />
                <span className="text-[13px]">AP spend (paid)</span>
              </div>
              <span className="font-mono text-[14px] text-danger">−{fmtMoney(stats.ap_spend_mtd)}</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-warn" />
                <span className="text-[13px]">Fixed OPEX</span>
              </div>
              <span className="font-mono text-[14px] text-warn">−{fmtMoney(stats.monthly_burn)}</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-danger" />
                <span className="text-[13px]">Payroll (this month)</span>
              </div>
              <span className="font-mono text-[14px] text-danger">−{fmtMoney(stats.payroll_mtd)}</span>
            </div>
            {stats.last_run && (
              <div className="pl-4 -mt-1.5 text-[11px] text-text-dim">
                {stats.payroll_mtd === 0
                  ? `No pay run paid this month yet — last run ${fmtMoney(stats.last_run.total)} paid ${fmtDate(stats.last_run.paid_date)}`
                  : `Last run ${fmtMoney(stats.last_run.total)} paid ${fmtDate(stats.last_run.paid_date)}`}
              </div>
            )}
            <div className="pt-3 border-t border-line flex items-center justify-between">
              <span className="font-semibold text-[13px]">Net</span>
              <span className={`font-mono text-[16px] font-semibold ${net_month >= 0 ? "text-ok" : "text-danger"}`}>
                {net_month >= 0 ? "+" : ""}{fmtMoney(net_month)}
              </span>
            </div>
          </div>
        </Card>

        {/* AP pending + quick links */}
        <Card>
          <div className="p-4 border-b border-line">
            <h3 className="font-semibold">Action required</h3>
          </div>
          <div className="p-4 space-y-3">
            {stats.ap_pending > 0 && (
              <Link to="/ap" className="flex items-center justify-between p-3 bg-warn/10 border border-warn/20 rounded hover:bg-warn/15">
                <div>
                  <div className="text-[13px] font-medium">AP invoices pending review</div>
                  <div className="text-[11px] text-text-muted">{fmtMoney(stats.ap_pending)} waiting</div>
                </div>
                <ChevronRight size={14} className="text-warn" />
              </Link>
            )}
            {stats.overdue_ar > 0 && (
              <Link to="/invoices" className="flex items-center justify-between p-3 bg-danger/10 border border-danger/20 rounded hover:bg-danger/15">
                <div>
                  <div className="text-[13px] font-medium">Overdue invoices</div>
                  <div className="text-[11px] text-text-muted">{fmtMoney(stats.overdue_ar)} past due</div>
                </div>
                <ChevronRight size={14} className="text-danger" />
              </Link>
            )}
            <Link to="/quotes?status=draft" className="flex items-center justify-between p-3 bg-ink-raised border border-line rounded hover:bg-ink-hover">
              <div>
                <div className="text-[13px] font-medium">Draft quotes to finish</div>
                <div className="text-[11px] text-text-muted">Build and send</div>
              </div>
              <ChevronRight size={14} className="text-text-muted" />
            </Link>
            {stats.ap_pending === 0 && stats.overdue_ar === 0 && (
              <div className="py-4 text-center text-[12px] text-text-muted">Nothing urgent.</div>
            )}
          </div>
        </Card>
      </div>

      {/* Top projects by margin */}
      <Card>
        <div className="p-4 border-b border-line flex items-center justify-between">
          <h3 className="font-semibold">Active projects — margin tracking</h3>
          <Link to="/projects"><Button variant="ghost" size="sm">All projects <ChevronRight size={12}/></Button></Link>
        </div>
        {stats.project_financials.length === 0 ? (
          <EmptyState title="No active projects" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead><tr className="border-b border-line">
                {["Project", "Contract", "Invoiced", "AP spend", "Est. margin", "%"].map(h => (
                  <th key={h} className="text-left px-4 py-2 text-[10px] font-medium uppercase tracking-wider2 text-text-muted">{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {stats.project_financials.map((p: any, i: number) => (
                  <tr key={p.project_id} className={`border-b border-line last:border-b-0 cursor-pointer hover:bg-ink-hover ${i % 2 ? "bg-ink/40" : ""}`}
                    onClick={() => window.location.href = `/projects/${p.project_id}`}>
                    <td className="px-4 py-2">
                      <div className="font-mono text-[11px] text-accent">{p.project_code}</div>
                      <div className="text-[12px] truncate max-w-[240px]">{p.project_name}</div>
                    </td>
                    <td className="px-4 py-2 font-mono">{fmtMoney(p.contract_value)}</td>
                    <td className="px-4 py-2 font-mono">{fmtMoney(p.invoiced_total)}</td>
                    <td className="px-4 py-2 font-mono text-text-muted">{fmtMoney(p.ap_spend)}</td>
                    <td className="px-4 py-2 font-mono">{fmtMoney(p.estimated_margin)}</td>
                    <td className={`px-4 py-2 font-mono text-right ${
                      p.estimated_margin_percent >= 30 ? "text-ok" :
                      p.estimated_margin_percent >= 15 ? "text-warn" : "text-danger"
                    }`}>
                      {p.estimated_margin_percent != null ? `${p.estimated_margin_percent.toFixed(1)}%` : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <div className="mt-4 flex items-center gap-2 flex-wrap">
        <Link to="/rates"><Button variant="secondary" size="sm" icon={BarChart3}>Rate cards</Button></Link>
        <Link to="/opex"><Button variant="secondary" size="sm" icon={Receipt}>OPEX tracker</Button></Link>
        {/* mig 385 — what the warehouse costs to run, and next year's forecast */}
        <Link to="/commercial/premises"><Button variant="secondary" size="sm" icon={Building2}>Premises</Button></Link>
        {/* mig 397 — what the fleet costs to run, by account and by vehicle */}
        <Link to="/commercial/vehicles"><Button variant="secondary" size="sm" icon={Truck}>Vehicles</Button></Link>
        {/* mig 399 — the loan book: schedules, interest, balloons, what is due (director only) */}
        <Link to="/commercial/loans"><Button variant="secondary" size="sm" icon={Landmark}>Loans</Button></Link>
        {/* mig 403 — the third bucket: premiums by policy and by account (director only) */}
        <Link to="/commercial/insurance"><Button variant="secondary" size="sm" icon={ShieldCheck}>Insurance</Button></Link>
        <Link to="/suppliers"><Button variant="secondary" size="sm" icon={Briefcase}>Suppliers</Button></Link>
      </div>
    </div>
  );
}

function firstOfMonth(): string {
  // Local-date build. The old version did d.setDate(1) then toISOString(), which
  // converts to UTC — in Brisbane (UTC+10) that rolled the boundary back to the
  // LAST day of the previous month whenever it ran before 10am, so "this month"
  // silently started a day early (and only some of the time). Format the local
  // parts directly so the boundary is always the true 1st, local time.
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}

function avgMargin(quotes: any[]): number {
  const withMargin = quotes.filter(q => q.margin_percent != null);
  if (withMargin.length === 0) return 0;
  return withMargin.reduce((a, q) => a + q.margin_percent, 0) / withMargin.length;
}
